import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  TrendingUp,
  BarChart3,
  Landmark,
  Gamepad2
} from 'lucide-react';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "How to Invest: A Beginner's Guide to Building Wealth",
  "description": "Learn the fundamentals of investing, from stocks and ETFs to bonds, and discover how hands-on practice can accelerate your journey.",
  "url": "https://wallstreetle.com/blog/how-to-invest",
  "datePublished": "2026-03-20",
  "dateModified": "2026-03-20",
  "author": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "publisher": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "image": "https://wallstreetle.com/assets/share-link-image.png"
};

export default function HowToInvestPage() {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | How to Invest</title>
        <meta
          name="description"
          content="Learn how to invest as a complete beginner. Understand stocks, ETFs, bonds, and more, then practice with Wallstreetle's free daily stock puzzles."
        />
        <meta
          name="keywords"
          content="how to invest, investing for beginners, beginner investing guide, how to buy stocks, how to start investing, stock market basics, investment types, ETFs, bonds, learn investing"
        />
        <link rel="canonical" href="https://wallstreetle.com/blog/how-to-invest" />

        <meta property="og:title" content="Wallstreetle | How to Invest" />
        <meta property="og:description" content="Learn the fundamentals of investing: stocks, ETFs, and bonds. Practice with Wallstreetle's free daily stock puzzles." />
        <meta property="og:url" content="https://wallstreetle.com/blog/how-to-invest" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | How to Invest" />
        <meta property="twitter:description" content="Learn the fundamentals of investing and practice with free daily stock puzzles." />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <TrendingUp className="w-4 h-4" /> Beginner Guide
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
            How to Invest
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A practical, no-jargon guide to getting started with investing, even if you've never bought a single share.
          </p>
        </div>

        {/* Section: Why Invest */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Why should you invest?</h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              Money sitting in a savings account loses value over time due to inflation. Investing puts your money to work, allowing it to grow through compound returns. Historically, the stock market has returned roughly 7–10% per year on average, far outpacing inflation.
            </p>
            <p>
              You don't need to be wealthy to start. Many brokerages allow you to invest with as little as $1 through fractional shares. The most important step is simply getting started.
            </p>
          </div>
        </section>

        {/* Section: Getting Started */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Getting started in 4 steps</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { step: '1', title: 'Set a goal', desc: 'Retirement, a house, or general wealth building. Knowing your goal shapes your strategy.' },
              { step: '2', title: 'Build an emergency fund', desc: 'Before investing, set aside 3–6 months of expenses so you won\'t need to sell at the wrong time.' },
              { step: '3', title: 'Open a brokerage account', desc: 'Choose a low-fee brokerage (Fidelity, Schwab, Robinhood) and fund it with an amount you\'re comfortable with.' },
              { step: '4', title: 'Start small and learn', desc: 'Begin with index funds or blue-chip stocks. Learn as you go. Practice makes all the difference.' },
            ].map((item) => {
              return (
                <Card key={item.step} className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 font-bold text-sm shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Section: Types of Investments */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Types of investments</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {[
              { title: 'Stocks', desc: 'Own a piece of a company. Higher potential returns, but more volatile. Great for long-term growth.', icon: TrendingUp, color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' },
              { title: 'ETFs & Index Funds', desc: 'Bundles of stocks that track a market index. Instant diversification with low fees.', icon: BarChart3, color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' },
              { title: 'Bonds', desc: 'Loans to governments or companies that pay fixed interest. Lower risk, lower returns.', icon: Landmark, color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="p-6 hover:shadow-lg transition-shadow">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${item.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Section: How Wallstreetle Helps */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Learn by doing with Wallstreetle</h2>
          <Card className="p-6 sm:p-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg shrink-0">
                <Gamepad2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Reading about investing is helpful, but <strong className="text-foreground">nothing beats hands-on practice</strong>. Wallstreetle gives you a daily stock puzzle where you analyze real financial data like revenue, P/E ratios, and market cap to identify a mystery company.
                </p>
                <p>
                  It's like flashcards for the stock market. Over time, you'll naturally learn to recognize financial patterns and build the instincts that real investors rely on.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">
            Start your investing education today
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Play a free daily stock puzzle and learn to read financial data like a pro.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            Play Wallstreetle Free
          </Link>
        </Card>
      </div>
    </>
  );
}
